import React, { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import Button from './Button';
import './UpdateToast.css';

// Durée d'affichage du toast "prêt hors-ligne" avant masquage automatique.
const OFFLINE_READY_TIMEOUT_MS = 4000;

// Fréquence à laquelle on demande au navigateur de vérifier une nouvelle version
// du service worker, sans attendre un rechargement de page (utile pour une PWA
// installée qui reste ouverte longtemps). 1 heure.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Toast d'information sur l'état du service worker.
 * - Nouvelle version disponible -> propose de recharger (l'utilisateur garde la main).
 * - Première mise en cache -> confirme brièvement que l'appli est prête hors-ligne.
 *
 * S'appuie sur le hook officiel de vite-plugin-pwa (mode registerType: 'prompt').
 */
const UpdateToast: React.FC = () => {
    const {
        needRefresh: [needRefresh, setNeedRefresh],
        offlineReady: [offlineReady, setOfflineReady],
        updateServiceWorker,
    } = useRegisterSW({
        // Vérifie périodiquement l'existence d'une nouvelle version : si le SW
        // distant a changé, `needRefresh` passe à true et le toast apparaît,
        // même si l'utilisateur n'a pas rechargé la page.
        onRegisteredSW(swUrl, registration) {
            if (!registration) {
                return;
            }
            setInterval(async () => {
                if (registration.installing || !navigator.onLine) {
                    return;
                }
                try {
                    const resp = await fetch(swUrl, {
                        cache: 'no-store',
                        headers: { 'cache-control': 'no-cache' },
                    });
                    if (resp.status === 200) {
                        await registration.update();
                    }
                } catch {
                    // Hors-ligne ou erreur réseau : on réessaiera au prochain tick.
                }
            }, UPDATE_CHECK_INTERVAL_MS);
        },
        onRegisterError(error) {
            console.error('Service worker registration error:', error);
        },
    });

    // Le toast "prêt hors-ligne" s'efface seul après quelques secondes.
    useEffect(() => {
        if (!offlineReady) {
            return;
        }
        const timer = window.setTimeout(() => setOfflineReady(false), OFFLINE_READY_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [offlineReady, setOfflineReady]);

    if (needRefresh) {
        return (
            <div className="UpdateToast" role="alert">
                <span className="UpdateToast-text">Une nouvelle version est disponible.</span>
                <div className="UpdateToast-actions">
                    <Button variant="primary" size="small" onClick={() => updateServiceWorker(true)}>
                        Recharger
                    </Button>
                </div>
            </div>
        );
    }

    if (offlineReady) {
        return (
            <div className="UpdateToast UpdateToast--info" role="status">
                <span className="UpdateToast-text">Appli prête pour le hors-ligne.</span>
                <button
                    type="button"
                    className="UpdateToast-close"
                    aria-label="Fermer"
                    onClick={() => setOfflineReady(false)}
                >
                    ×
                </button>
            </div>
        );
    }

    return null;
};

export default UpdateToast;
